import React, { useEffect, useState } from 'react';
import DashboardLayout from '../../layouts/DashboardLayout';
import CompetencyTrajectoryChart from '../../components/CompetencyTrajectoryChart';
import api from '../../api';
import { AlertCircle, TrendingUp } from 'lucide-react';

export default function LongitudinalReport() {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [doctors, setDoctors] = useState([]);
    const [selectedDoctor, setSelectedDoctor] = useState("");
    const [error, setError] = useState("");

    useEffect(() => {
        const loadDoctors = async () => {
            try {
                const res = await api.getDoctorsList();
                setDoctors(res.data?.doctors || []);
                console.log("Loaded doctors for filter:", res.data);
            } catch (err) {
                console.error("Failed to load doctors", err);
            }
        };
        loadDoctors();
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                // Fetch all therapy types, optionally filtered by doctor
                const res = await api.getCompetencyTrajectory(selectedDoctor || null, null);
                setData(res.data); // Returns { "CBT": {...}, "SFBT": {...} }
            } catch (err) {
                console.error("Failed to fetch longitudinal data:", err);
                setError("Unable to load competency history.");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [selectedDoctor]);

    return (
        <DashboardLayout>
            <div className="min-h-screen bg-gray-50/50 dark:bg-gray-900 p-6">

                {/* Header Section */}
                <div className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                            <TrendingUp className="w-6 h-6 text-indigo-600" />
                            Longitudinal Competency Report
                        </h1>
                        <p className="text-gray-500 mt-1">
                            Track skill emergence and stability over time across your sessions.
                        </p>
                    </div>

                    {/* Doctor Filter (Admin/Director View) */}
                    <div className="w-full md:w-64">
                        <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase">Filter by Doctor</label>
                        <select
                            className="w-full px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all text-sm"
                            value={selectedDoctor}
                            onChange={(e) => setSelectedDoctor(e.target.value)}
                        >
                            <option value="">All Doctors (Org Wide)</option>
                            {doctors.map(doc => (
                                <option key={doc.id} value={doc.id}>
                                    {doc.name}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Philosophy Card - "One sentence you should literally put in the UI" */}
                <div className="bg-indigo-50 dark:bg-indigo-900/20 border-l-4 border-indigo-500 p-4 mb-8 rounded-r-lg shadow-sm">
                    <div className="flex gap-3">
                        <AlertCircle className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                        <div>
                            <p className="text-indigo-900 dark:text-indigo-200 font-medium text-sm">
                                Early-session scores reflect skill emergence, not failure. Competence is evaluated across trajectories, not single sessions.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Loading / Error States */}
                {loading && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[1, 2, 3].map(i => (
                            <div key={i} className="h-64 bg-white dark:bg-gray-800 rounded-xl shadow-sm animate-pulse" />
                        ))}
                    </div>
                )}

                {error && (
                    <div className="p-4 bg-red-50 text-red-600 rounded-lg">
                        {error}
                    </div>
                )}

                {/* Charts Grid */}
                {!loading && data && (
                    <div className="space-y-12">
                        {Object.entries(data).map(([tType, tData]) => (
                            <div key={tType} className="animate-fade-in-up">
                                <h2 className="text-xl font-bold text-secondary-800 mb-4 px-1 border-l-4 border-primary-500 pl-3">
                                    {tType} Trajectory
                                </h2>

                                {tData.competencies && Object.keys(tData.competencies).length > 0 ? (
                                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                                        {Object.entries(tData.competencies).map(([compName, compData]) => (
                                            <CompetencyTrajectoryChart
                                                key={compName}
                                                title={compName.replace(/_/g, ' ')}
                                                data={compData.timeline}
                                                status={compData.status}
                                                average={compData.average}
                                                currentScore={compData.current_score}
                                                scaleMax={tData.scale_max}
                                                threshold={tData.satisfactory_threshold}
                                            />
                                        ))}
                                    </div>
                                ) : (
                                    <div className="p-6 bg-white dark:bg-gray-800 rounded-lg border border-dashed border-gray-300 text-center text-gray-500 italic">
                                        No competency data available for {tType}.
                                    </div>
                                )}
                            </div>
                        ))}

                        {Object.keys(data).length === 0 && (
                            <div className="text-center py-12 text-gray-500">
                                No longitudinal data available yet. Complete sessions to see your trajectory.
                            </div>
                        )}
                    </div>
                )}
            </div>
        </DashboardLayout>
    );
}
